import { Button, Card, Col, Form, Row } from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';

import { AuthContext } from 'components/Auth/Auth';
import ConfirmModal from 'components/theme_components/ThirdParty/ConfirmModal';
import type { FormInstance } from 'antd/es/form';
import GetLabel from 'DisplayFramework/components/GetLabel';
import IBasket from 'icons/IBasket';
import IPen from 'icons/IPen';
import ModalSFS from 'components/theme_components/ModalSFS';
import { PlusOutlined } from '@ant-design/icons';
import ProductAmortissementModal from './ProductModals/ProductAmortissementModal';
import React from 'react';
import Title from 'components/Title';

export type ProductAmortissementProps = {
  form: FormInstance;
  required: boolean;
  amortizationList: any[];
  setAmortizationList: (amortization: any[]) => void;
};

const ProductAmortissement = ({
  form,
  required,
  amortizationList,
  setAmortizationList,
}: ProductAmortissementProps) => {
  // Remove the local data state - use amortizationList prop instead
  const [modalVisible, setModalVisible] = useState(false);
  const [deleteKey, setDeleteKey] = useState<string | null>(null);
  const { isInspect } = useContext(AuthContext);
  const durationmax = Number(form.getFieldValue('durationmax') || 0);

  // Compute totalPercent and finpercent
  const finpercent = form.getFieldValue('finpercent') || 100;
  console.log('finpercent', finpercent);
  const totalPercent = useMemo(() => {
    return amortizationList.reduce((acc, item) => {
      const percent =
        (Number(item.cteamortizedcoef) || 0) *
        ((item.amtendday - item.amtstartday + 1) / item.cteperiodicity);
      return acc + percent;
    }, 0);
  }, [amortizationList]);

  const setVisible = (visible: boolean) => {
    setModalVisible(visible);
  };

  useEffect(() => {
    const depreciation = form.getFieldValue('depreciation');
    if (depreciation === 'infine') {
      const durationMax = form.getFieldValue('durationmax');
      const initialData = [
        {
          key: 1,
          amtstartday: 1,
          amtendday: durationMax,
          cteperiodicity: durationMax,
          amortbillingday: durationMax,
          cteamortizedcoef: 100,
        },
      ];
      setAmortizationList(initialData);
      form.setFieldsValue({
        amortization: initialData,
      });
    } else {
      if (amortizationList.length > 0) {
        form.setFieldsValue({
          amortization: amortizationList,
        });
      }
    }
  }, [
    form,
    form.getFieldValue('depreciation'),
    form.getFieldValue('durationmax'),
    setAmortizationList,
  ]);

  /* const addCard = (newCard: any) => {
    const updatedData = [
      ...amortizationList,
      { key: (amortizationList.length + 1).toString(), ...newCard },
    ];
    setAmortizationList(updatedData);
  };*/
  const addCard = (newCard: any) => {
    const updatedData = [
      ...amortizationList,
      {
        key: (amortizationList.length + 1).toString(),
        ...newCard,
      },
    ];
    setAmortizationList(updatedData);
    form.setFieldsValue({ amortization: updatedData });
  };

  const deleteCard = (key: string) => {
    const updatedData = amortizationList.filter((item) => String(item.key) !== String(key));
    setAmortizationList(updatedData);
    form.setFieldsValue({ amortization: updatedData });
  };

  const updateCard = (updatedCard: any) => {
    // Update the edited period with new values (match by string key)
    let updatedData = amortizationList.map((dataItem) =>
      String(dataItem.key) === String(updatedCard.key)
        ? { ...dataItem, ...updatedCard }
        : { ...dataItem }
    );

    // Adjust start days for all periods after the edited one
    const editedIdx = updatedData.findIndex(item => String(item.key) === String(updatedCard.key));
    for (let i = editedIdx + 1; i < updatedData.length; i++) {
      updatedData[i].amtstartday = updatedData[i - 1].amtendday + 1;
      if (updatedData[i].amtendday < updatedData[i].amtstartday) {
        updatedData[i].amtendday = updatedData[i].amtstartday;
      }
    }

    // Only recalculate the last period's coef if a middle period is edited
    const lastIdx = updatedData.length - 1;
    if (updatedData.length > 1 && editedIdx !== lastIdx) {
      let totalPercent = 0;
      for (let i = 0; i < updatedData.length - 1; i++) {
        const periodLength = updatedData[i].amtendday - updatedData[i].amtstartday + 1;
        const periodicity = updatedData[i].cteperiodicity;
        totalPercent += (Number(updatedData[i].cteamortizedcoef) || 0) * (periodLength / periodicity);
      }
      const last = updatedData[lastIdx];
      const lastPeriodLength = last.amtendday - last.amtstartday + 1;
      const lastPeriodicity = last.cteperiodicity;
      const remainingPercent = finpercent - totalPercent;
      if (lastPeriodLength > 0 && lastPeriodicity > 0) {
        const newCoef = Number((remainingPercent / (lastPeriodLength / lastPeriodicity)).toFixed(4));
        updatedData[lastIdx].cteamortizedcoef = newCoef >= 0 ? newCoef : 0;
      }
    }

    setAmortizationList(updatedData);
    form.setFieldsValue({ amortization: updatedData });
  };

  const isInfine = form.getFieldValue('depreciation') === 'INFINE';

  return (
    <Form form={form}>
      <div className="container-stepper mt-4 taux productlist">
        <Title
          comcode="infoToCompleteAmortissement"
          level={4}
          className="ut-updateTP-title mb-7 mt-10"
        />
        <Row
          className="mb-4 px-6 text-center"
          gutter={24}
          justify="space-between"
          align="middle"
        >
          <Col span={4}>
            <GetLabel
              label="Debut"
              className=" font-size-13 font-weight-600 gray-color"
            />
          </Col>
          <Col span={4}>
            <GetLabel
              label="Fin"
              className=" font-size-13 font-weight-600 gray-color"
            />
          </Col>
          <Col span={4}>
            <GetLabel
              label="periodicity"
              className=" font-size-13 font-weight-600 gray-color"
            />
          </Col>
          <Col span={5}>
            <GetLabel
              label="invoiceday"
              className=" font-size-13 font-weight-600 gray-color"
            />
          </Col>
          <Col span={5}>
            <GetLabel
              label="RateAmount"
              className=" font-size-13 font-weight-600 gray-color"
            />
          </Col>
          <Col span={2}></Col>
        </Row>

        {/* Use amortizationList instead of data */}
        {(!amortizationList || amortizationList.length === 0) && (
          <Card
            style={{ zIndex: 100 }}
            className="no-border-radius no-border text-center mb-4 textCard "
          >
            <GetLabel
              label="amortissementVid"
              className="font-size-13 gray-color "
            />
          </Card>
        )}

        {/* Use amortizationList instead of data */}
        {amortizationList &&
          amortizationList.map((item) => (
            <Card
              key={item.key}
              className="activity-card mb-4 text-center"
              bordered={false}
            >
              <Row gutter={24} align="middle" justify="space-between">
                <Col span={4} className="text-middle">
                  {item.amtstartday || '-'}
                </Col>
                <Col span={4} className="text-middle">
                  {item.amtendday || '-'}
                </Col>
                <Col span={4} className="text-middle">
                  {item.cteperiodicity || '-'}
                </Col>
                <Col span={5} className="text-middle">
                  {item.amortbillingday || '-'}
                </Col>
                <Col span={5}>
                  <span className="gray-color">
                    <GetLabel label="rate" />
                  </span>{' '}
                  {item.cteamortizedcoef || '-'} %
                </Col>
                <Col span={2} className="pl-0 pr-0">
                  <IBasket
                    color="#DD7248"
                    className="IBasket"
                    onClick={() => {
                      setDeleteKey(item.key);
                      setModalVisible(true);
                    }}
                  />
                  <ModalSFS
                    className="color-gray"
                    width={560}
                    trigger={({ setVisible }) => (
                      <IPen
                        className="IPen"
                        onClick={() => {
                          setVisible(true);
                        }}
                      />
                    )}
                    title="updateAmortissement"
                  >
                    <ProductAmortissementModal
                      mode="update"
                      data={item}
                      initamount={form.getFieldValue('partialamount')}
                      setVisible={setVisible}
                      onUpdateCard={updateCard}
                      durationmax={durationmax}
                      finpercent={finpercent || 100}
                      allPeriods={amortizationList}
                    />
                  </ModalSFS>
                </Col>
              </Row>
            </Card>
          ))}

        {!isInfine && (
          <Card
            style={{ zIndex: 100 }}
            className="no-border-radius no-border text-center mb-4 "
          >
            <GetLabel
              label="NBAmort"
              className="font-size-13 font-weight-500 "
            />
          </Card>
        )}

        <Row justify="center" align="middle">
          {!isInfine && (
            <ModalSFS
              trigger={({ setVisible }) => (
                <Button
                  className="ut-reset-btn ut-btn-no-border no-box-shadow font-weight-500"
                  onClick={() => {
                    form.validateFields().then(() => {
                      setVisible(true);
                    });
                  }}
                  disabled={
                    totalPercent >= finpercent ||
                    (amortizationList.length > 0 && amortizationList[amortizationList.length - 1].amtendday >= durationmax)
                  }
                  icon={<PlusOutlined />}
                >
                  <GetLabel label="addAmortissement" />
                </Button>
              )}
              title="CreateAmortissement"
            >
              <ProductAmortissementModal
                mode="create"
                data={amortizationList}
                initamount={form.getFieldValue('partialamount')}
                setVisible={setVisible}
                onAddCard={addCard}
                durationmax={durationmax}
                defaultStartDay={
                  amortizationList.length > 0
                    ? amortizationList[amortizationList.length - 1].amtendday + 1
                    : 1
                }
                defaultEndDay={durationmax}
                finpercent={finpercent || 100}
                allPeriods={amortizationList}
              />
            </ModalSFS>
          )}
          <ConfirmModal
            visible={modalVisible}
            status="error"
            message="Êtes-vous sûr de vouloir supprimer l'amortissement ? "
            onConfirm={() => {
              if (deleteKey !== null) {
                deleteCard(deleteKey);
                setDeleteKey(null);
              }
              setModalVisible(false);
            }}
            onCancel={() => {
              setDeleteKey(null);
              !isInspect && setModalVisible(false);
            }}
            className="modalConfirmOrCancel"
          />
        </Row>
      </div>
    </Form>
  );
};

export default ProductAmortissement;
